import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Theme, makeStyles, createStyles, Typography } from '@bb-ui/react-library';
import { DialogProps } from '@bb-ui/react-library/dist/components/Dialog/Dialog.types';
import {
  DefaultButton,
  PrimaryButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  SearchBox,
} from '@bb-ui/react-library/dist/components';
import { useSnackbar } from 'hooks/useSnackbar';
import { useRestApi } from 'hooks/useRestApi';
import { apiUrl } from 'utils/apiUrl';
import { orderBy, chunk } from 'lodash';
import {
  AUTHORIZATION_SERVICES,
  AUTHORIZATION_PERMISSIONS,
  AuthorizationService,
} from 'hooks/useAuthorization.types';

const CATEGORIES = ['Houston', 'Learn Cloud Tooling(LCT)', 'General'] as const;
type Category = (typeof CATEGORIES)[number];

const getCategory = (perm: string): Category => {
  if (AUTHORIZATION_SERVICES.some((s) => perm.startsWith(`${s}.`))) return 'Houston';
  if (perm.startsWith('lct.')) return 'Learn Cloud Tooling(LCT)';
  return 'General';
};

const ROW_HEIGHT = 48;
const SEARCH_HEIGHT = 56;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    addGroupInput: {
      margin: theme.spacing(2, 0, 1),
    },
    modalContent: {
      display: 'flex',
    },
    categoryList: {
      width: '14rem',
      borderRight: `1px solid ${theme.palette.grey[300]}`,
      overflowY: 'auto',
    },
    categoryHeader: {
      padding: theme.spacing(1, 2),
    },
    categoryItem: {
      padding: theme.spacing(1, 2),
      cursor: 'pointer',
    },
    categoryItemSelected: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    permissionsContainer: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      paddingLeft: theme.spacing(2),
    },
    uiHeader: {
      display: 'flex',
      marginBottom: theme.spacing(1),
    },
    uiContainer: {
      display: 'flex',
      flex: 1,
      overflow: 'hidden',
    },
    serviceList: {
      width: '12rem',
      borderRight: `1px solid ${theme.palette.grey[300]}`,
      overflowY: 'auto',
    },
    serviceItem: {
      padding: theme.spacing(1, 2),
      cursor: 'pointer',
    },
    serviceItemSelected: {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText,
    },
    permissionList: {
      flex: 1,
      overflowY: 'auto',
      padding: theme.spacing(1),
    },
    searchWrapper: {
      height: SEARCH_HEIGHT,
      display: 'flex',
      alignItems: 'center',
    },
    tableWrapper: {
      flex: 1,
      overflowY: 'auto',
    },
    row: {
      display: 'flex',
      height: ROW_HEIGHT,
    },
    clickableCell: {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      flex: '0 0 25%',
      paddingRight: theme.spacing(2),
      '&:hover': {
        backgroundColor: theme.palette.grey[200],
      },
    },
  }),
);

export interface CreateGroupDialogProps extends DialogProps {
  id: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateGroupDialog: React.FC<CreateGroupDialogProps> = ({
  id,
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const {
    clearFailedRequests,
    clearSucceededRequests,
    doPost,
    failedRequests,
    loadingRequests,
    succeededRequests,
  } = useRestApi(apiUrl('sso', 'fndsGroups'), { manual: true });

  const { data: apiData, fetch: fetchPermissions } = useRestApi(apiUrl('authz', 'permissions'), {
    manual: true,
  });

  React.useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const apiPermissions = React.useMemo<string[]>(
    () => (Array.isArray(apiData) ? apiData : (apiData?.permissions ?? [])),
    [apiData],
  );

  const mergedPermissions = React.useMemo(
    () =>
      Array.from(
        new Set([
          ...AUTHORIZATION_SERVICES.flatMap((s) =>
            AUTHORIZATION_PERMISSIONS.map((p) => `${s}.${p}`),
          ),
          ...apiPermissions,
        ]),
      ),
    [apiPermissions],
  );

  const [groupName, setGroupName] = React.useState('');
  const [groupType, setGroupType] = React.useState('');
  const [groupDesc, setGroupDesc] = React.useState('');
  const [selectedPermissions, setSelectedPermissions] = React.useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = React.useState('');
  const [showPermissionsModal, setShowPermissionsModal] = React.useState(false);
  const [showErrors, setShowErrors] = React.useState(false);
  const [category, setCategory] = React.useState<Category>('Houston');
  const [selectedService, setSelectedService] = React.useState<AuthorizationService>(
    AUTHORIZATION_SERVICES[0],
  );

  React.useEffect(() => {
    if (!open) {
      setGroupName('');
      setGroupType('');
      setGroupDesc('');
      setSelectedPermissions(new Set());
      setShowErrors(false);
      setCategory('Houston');
      setSelectedService(AUTHORIZATION_SERVICES[0]);
      setSearchTerm('');
    }
  }, [open]);

  React.useEffect(() => {
    if (succeededRequests.length) {
      enqueueSnackbar(
        t('groupPermissionsDialog.successMessage', { name: groupName, action: 'created' }),
        { variant: 'success' },
      );
      clearSucceededRequests();
      onSuccess();
      onClose();
    }
  }, [
    succeededRequests,
    clearSucceededRequests,
    enqueueSnackbar,
    onClose,
    groupName,
    t,
    onSuccess,
  ]);

  React.useEffect(() => {
    if (failedRequests.length) {
      enqueueSnackbar(
        t('groupPermissionsDialog.failureMessage', {
          name: groupName,
          action: 'created',
          message: failedRequests[0].error.message,
        }),
        { variant: 'error' },
      );
      clearFailedRequests();
      onClose();
    }
  }, [failedRequests, clearFailedRequests, enqueueSnackbar, onClose, groupName, t]);

  const selectedCount = (cat: Category) =>
    Array.from(selectedPermissions).filter((p) => getCategory(p) === cat).length;

  const selectedServiceCount = (service: AuthorizationService) =>
    Array.from(selectedPermissions).filter((p) => p.startsWith(`${service}.`)).length;

  const togglePermission = (perm: string) => {
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(perm)) next.delete(perm);
      else next.add(perm);
      return next;
    });
  };

  const createGroup = (e: React.FormEvent) => {
    e.preventDefault();
    setShowErrors(true);
    if (!groupName || !groupType || !groupDesc) return;
    doPost({
      name: groupName.trim(),
      type: groupType.trim(),
      description: groupDesc.trim(),
      permissions: Array.from(selectedPermissions),
    });
  };

  const rowsPerCategory = React.useMemo(
    () =>
      CATEGORIES.map(
        (cat) => Math.ceil(mergedPermissions.filter((p) => getCategory(p) === cat).length / 4) || 1,
      ),
    [mergedPermissions],
  );
  const maxRows = Math.max(...rowsPerCategory);
  const tableHeight = maxRows * ROW_HEIGHT;

  const filtered = React.useMemo(() => {
    const perms = mergedPermissions
      .filter((p) => getCategory(p) === category)
      .filter((p) => p.toLowerCase().includes(searchTerm.toLowerCase()));
    return chunk(
      orderBy(perms, (p) => p.toLowerCase(), ['asc']),
      4,
    );
  }, [mergedPermissions, category, searchTerm]);

  return (
    <Dialog id={id} open={open} onClose={onClose}>
      <DialogTitle onClose={onClose} closeLabel={t('global.close')}>
        {t('groupPermissionsDialog.title', { dialogType: 'Add new' })}
      </DialogTitle>

      {/* Form to capture group details */}
      <form onSubmit={createGroup} noValidate>
        <DialogContent>
          <TextField
            id="groupName"
            className={classes.addGroupInput}
            label={t('groupPermissionsDialog.groupName')}
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            fullWidth
            required
            error={showErrors && !groupName}
          />
          <TextField
            id="groupType"
            className={classes.addGroupInput}
            label={t('groupPermissionsDialog.groupType')}
            value={groupType}
            onChange={(e) => setGroupType(e.target.value)}
            fullWidth
            required
            error={showErrors && !groupType}
          />
          <TextField
            id="groupDesc"
            className={classes.addGroupInput}
            label={t('groupPermissionsDialog.groupDesc')}
            value={groupDesc}
            onChange={(e) => setGroupDesc(e.target.value)}
            fullWidth
            multiline
            required
            error={showErrors && !groupDesc}
          />

          {/* Button to open the permissions selection modal */}
          <DefaultButton onClick={() => setShowPermissionsModal(true)}>
            {t('groupPermissionsDialog.selectPermissions', { count: selectedPermissions.size })}
          </DefaultButton>

          {/* Helper text: summary of selected permissions */}
          <Typography variant="body2" color="textSecondary">
            {selectedPermissions.size > 0
              ? t('groupPermissionsDialog.permissionsSelected', { count: selectedPermissions.size })
              : t('groupPermissionsDialog.noPermissionsSelected')}
          </Typography>
        </DialogContent>

        {/* Form action buttons: Cancel and Save */}
        <DialogActions>
          <DefaultButton onClick={onClose}>{t('global.cancel')}</DefaultButton>
          <PrimaryButton type="submit" disabled={loadingRequests}>
            {t('global.save')}
          </PrimaryButton>
        </DialogActions>
      </form>

      {/* Permissions selection modal */}
      <Dialog
        open={showPermissionsModal}
        onClose={() => setShowPermissionsModal(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{ style: { height: `calc(100vh - 200px)` } }}
      >
        {/* Modal title displaying current count of selected permissions */}
        <DialogTitle onClose={() => setShowPermissionsModal(false)} closeLabel={t('global.close')}>
          {t('groupPermissionsDialog.selectPermissions', { count: selectedPermissions.size })}
        </DialogTitle>

        {/* Container divided into a categories list and permissions content */}
        <DialogContent>
          <div className={classes.modalContent}>
            {/* Categories list section */}
            <div className={classes.categoryList}>
              <Typography
                variant="subtitle1"
                className={classes.categoryHeader}
                style={{ fontWeight: 'bold' }}
              >
                {t('groupPermissionsDialog.categories')}
              </Typography>

              {CATEGORIES.map((cat) => (
                // Each category element with a count of selected permissions when applicable
                <div
                  key={cat}
                  className={[
                    classes.categoryItem,
                    category === cat && classes.categoryItemSelected,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setCategory(cat)}
                >
                  {cat}
                  {selectedCount(cat) > 0 && ` (${selectedCount(cat)})`}
                </div>
              ))}
            </div>

            {/* Permissions display section */}
            <div className={classes.permissionsContainer}>
              {category === 'Houston' ? (
                <>
                  {/* Header row for services and permissions */}
                  <div className={classes.uiHeader}>
                    <Typography variant="subtitle1" style={{ width: '12rem', fontWeight: 'bold' }}>
                      {t('groupPermissionsDialog.serviceHeader')}
                    </Typography>
                    <Typography variant="subtitle1" style={{ flex: 1, fontWeight: 'bold' }}>
                      {t('groupPermissionsDialog.permissionsHeader')}
                    </Typography>
                  </div>

                  {/* Container dividing the services list and permissions table */}
                  <div className={classes.uiContainer}>
                    {/* List of available services */}
                    <div className={classes.serviceList}>
                      {AUTHORIZATION_SERVICES.map((service) => (
                        // Each service element with selection highlighting if active
                        <div
                          key={service}
                          className={[
                            classes.serviceItem,
                            selectedService === service && classes.serviceItemSelected,
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          onClick={() => setSelectedService(service)}
                        >
                          {service}
                          {selectedServiceCount(service) > 0 &&
                            ` (${selectedServiceCount(service)})`}
                        </div>
                      ))}
                    </div>

                    {/* Table displaying permissions for the selected service */}
                    <div className={classes.permissionList}>
                      <Table>
                        <TableBody>
                          {AUTHORIZATION_PERMISSIONS.map((perm) => {
                            const key = `${selectedService}.${perm}`;
                            return (
                              <TableRow key={key} className={classes.row}>
                                <TableCell
                                  className={classes.clickableCell}
                                  onClick={() => togglePermission(key)}
                                >
                                  <Checkbox
                                    checked={selectedPermissions.has(key)}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => togglePermission(key)}
                                  />
                                  {perm}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Search bar to filter permissions */}
                  <div className={classes.searchWrapper}>
                    <SearchBox
                      inputId="search-permissions"
                      label={t('groupPermissionsDialog.searchPermissions')}
                      value={searchTerm}
                      onChange={setSearchTerm}
                      onSearch={setSearchTerm}
                    />
                  </div>

                  {/* Table displaying filtered permissions based on the search */}
                  <div className={classes.tableWrapper} style={{ height: tableHeight }}>
                    <Table>
                      <TableBody>
                        {filtered.map((row, idx) => (
                          <TableRow key={idx} className={classes.row}>
                            {row.map((perm) => (
                              <TableCell
                                key={perm}
                                className={classes.clickableCell}
                                onClick={() => togglePermission(perm)}
                              >
                                <Checkbox
                                  checked={selectedPermissions.has(perm)}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={() => togglePermission(perm)}
                                />
                                {perm}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};
